import React, { Suspense, useEffect } from 'react';
import clsx from 'clsx';
import { useMediaQuery } from 'react-responsive';
import { Outlet, useMatch, useNavigate, useParams } from 'react-router-dom';

import useAccountQuery from 'api/queries/useAccountQuery';
import useAislesQuery from 'api/queries/useAislesQuery';
import { WINE_AISLE_NAME } from 'constants/shop';
import DesktopShopNavigation from 'features/shop/navigation/DesktopShopNavigation';
import LinearAisleNav from 'features/shop/navigation/LinearAisleNav';
import { MobileL2PillNavigation } from 'features/shop/navigation/MobileL2PillNavigation';
import MobileShopNavigation from 'features/shop/navigation/MobileShopNavigation';
import WineFaqs from 'features/shop/WineFaqs';
import SearchAutocomplete from 'features/site/autocomplete/SearchAutocomplete';
import SearchInput from 'features/site/Header/SearchInput';
import Loader from 'honeydew/Loader';
import { FEATURE_FLAG_NAMES, useSplitOnOffFeatureFlag } from 'hooks/useFeatureFlag';
import useInactiveSubscription from 'hooks/useInactiveSubscription';
import useStickyShopNavigation, { STICKY_SHOP_NAVIGATION_STYLES } from 'hooks/useStickyShopNavigation';
import useTailwindTheme from 'hooks/useTailwindTheme';
import UpSellsCarousel from 'shared/UpSellsCarousel';
import { getDecodedPathSegment } from 'utils/pathUtils';

function ShopLayout() {
    const navigate = useNavigate();
    const { data: { aisles = [], inStockAllItemsCount } = {}, isLoading: isAislesLoading } = useAislesQuery();
    const { data: account = {} } = useAccountQuery();
    const tailwindTheme = useTailwindTheme();
    const mobile = useMediaQuery({ maxWidth: tailwindTheme?.screens?.md });
    const stickyShopNavigation = useStickyShopNavigation();

    const params = useParams();
    const isSearchRoute = !!useMatch('/shop/search/*');
    const isAisleRoute = !!useMatch('/shop/aisle/*');
    const isMemberDealsRoute = !!useMatch('/shop/aisle/member-deals');
    const isBuyItAgainRoute = !!useMatch('/shop/aisle/buy-it-again');
    const aisleDisplayName = getDecodedPathSegment(params?.category);
    const currentAisle = aisles?.find((aisle) => aisle.name.toLowerCase() === aisleDisplayName?.toLowerCase());
    const onWineAisle = currentAisle?.name === WINE_AISLE_NAME;
    const inactiveSubscription = useInactiveSubscription();
    const { value: hasUpSellsCarousel } = useSplitOnOffFeatureFlag(FEATURE_FLAG_NAMES.UP_SELLS_CAROUSEL_PLP);

    useEffect(() => {
        if (!account.subID) return;

        const goToAccountPage = () => navigate('/account');

        if (inactiveSubscription || !account.marketplaceOpen || (aisles?.length === 0 && !isAislesLoading))
            goToAccountPage();
    }, [inactiveSubscription, account.subID, aisles.length, isAislesLoading]);

    if (aisles?.length === 0) {
        return (
            <div className="mt-[140px] flex justify-center">
                <Loader />
            </div>
        );
    }

    return (
        <>
            <main className={clsx({ 'md:mt-80': !window.isNativeApp })}>
                {window.isNativeApp && (
                    <div className="fixed left-0 right-0 top-0 z-[1200] bg-white shadow-base">
                        <div className="px-25 py-15">
                            <SearchInput />
                        </div>
                        <SearchAutocomplete isShopRoute />
                    </div>
                )}
                {mobile ? (
                    <div
                        className={clsx('sticky z-50', {
                            'top-[56px] mt-[56px]': window.isNativeApp,
                            'top-0 duration-200': stickyShopNavigation.hideSiteNav,
                            [`${STICKY_SHOP_NAVIGATION_STYLES.top} ${STICKY_SHOP_NAVIGATION_STYLES.duration}`]:
                                !stickyShopNavigation.hideSiteNav,
                        })}
                    >
                        <MobileShopNavigation aisles={aisles} />
                    </div>
                ) : (
                    <div className="fixed bottom-0 left-0 top-80 w-[250px] shrink-0 overflow-y-scroll bg-malt p-20 if:bg-oat">
                        <DesktopShopNavigation aisles={aisles} inStockAllItemsCount={inStockAllItemsCount} />
                    </div>
                )}
                {(!isAisleRoute || isMemberDealsRoute || isBuyItAgainRoute) && <MobileL2PillNavigation />}
                <div className="min-h-[calc(100vh-80px)] bg-white pb-20 md:ml-[250px] md:px-20 md:pt-20">
                    <div className="max-w-7xl lg:mx-auto">
                        <Suspense fallback={<Loader />}>
                            <Outlet context={aisles} />
                            {!isSearchRoute && <LinearAisleNav aisles={aisles} />}
                            {onWineAisle && <WineFaqs />}
                        </Suspense>
                    </div>
                </div>
            </main>
            {hasUpSellsCarousel && !mobile && (
                <UpSellsCarousel name="Nice pick! Bet you'd like these too..." position="fixed" />
            )}
        </>
    );
}
export default ShopLayout;
